import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Trafic par page du site public d'un commerçant.
// Lit la table `theme_page_visits` (alimentée par l'Edge Function
// theme-visit-track qui capte la vraie IP du visiteur) et agrège :
//   • le compteur de visites par page ;
//   • les visiteurs uniques (IP distinctes) ;
//   • le temps passé (durée moyenne / totale) ;
//   • les produits les plus consultés (via le chemin /product/:id) ;
//   • le comportement récent (date, IP, page, événement, durée).
// Les données sont strictement limitées à la boutique du marchand connecté.
// ============================================================

export interface PageTrafficRow {
  pageKey: string;
  views: number;
  uniqueVisitors: number;
  avgTime: number;
  totalTime: number;
}

export interface ProductTrafficRow {
  productId: number;
  name: string;
  views: number;
}

export interface BehaviorRow {
  id: number;
  pageKey: string | null;
  path: string | null;
  ip: string | null;
  event: string;
  duration: number;
  at: string;
}

export interface TrafficSummary {
  totalViews: number;
  uniqueVisitors: number;
  avgTime: number;
  pages: PageTrafficRow[];
  topProducts: ProductTrafficRow[];
  behavior: BehaviorRow[];
}

const EMPTY: TrafficSummary = {
  totalViews: 0,
  uniqueVisitors: 0,
  avgTime: 0,
  pages: [],
  topProducts: [],
  behavior: [],
};

const PRETTY_PAGE: Record<string, string> = {
  home: "Page d'accueil",
  produits: 'Page produits',
  detailsproduit: 'Détail produit',
  contact: 'Contact',
  apropos: 'À propos',
  blog: 'Blog',
  detailsblog: 'Article de blog',
  panier: 'Panier',
  equipe: 'Équipe',
  faq: 'FAQ',
  gallery: 'Galerie',
  politique: 'Confidentialité',
  conditionsdutilisation: 'Conditions',
  header: 'En-tête (header)',
  footer: 'Pied de page',
  navmenu: 'Menu de navigation',
};

export function prettyPageLabel(pageKey: string | null, path: string | null): string {
  if (pageKey && PRETTY_PAGE[pageKey]) return PRETTY_PAGE[pageKey];
  if (pageKey) return pageKey;
  return path || 'Inconnue';
}

export function useThemeTraffic(days: number) {
  const { user } = useAuth();
  const [data, setData] = useState<TrafficSummary>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTraffic = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const since = new Date();
      since.setDate(since.getDate() - days);

      const { data: rows, error: err } = await supabase
        .from('theme_page_visits')
        .select('id, page_key, path, ip, event, duration_seconds, created_at')
        .eq('boutique_id', getCommerceId(user))
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false })
        .limit(5000);

      if (err) throw err;

      const list = (rows || []) as {
        id: number;
        page_key: string | null;
        path: string | null;
        ip: string | null;
        event: string;
        duration_seconds: number | null;
        created_at: string;
      }[];

      const viewRows = list.filter((r) => r.event === 'view');
      const ipSet = new Set<string>();
      list.forEach((r) => { if (r.ip) ipSet.add(r.ip); });

      // Agrégation par page (clé = page_key sinon path)
      const pageMap = new Map<string, { views: number; ips: Set<string>; totalTime: number; timeCount: number }>();
      viewRows.forEach((r) => {
        const key = r.page_key || r.path || 'inconnue';
        const entry = pageMap.get(key) || { views: 0, ips: new Set<string>(), totalTime: 0, timeCount: 0 };
        entry.views += 1;
        if (r.ip) entry.ips.add(r.ip);
        pageMap.set(key, entry);
      });
      // Temps passé : on associe les événements leave/ping à leur page
      list.forEach((r) => {
        if (r.event !== 'leave' && r.event !== 'ping') return;
        const dur = Number(r.duration_seconds) || 0;
        if (dur <= 0) return;
        const key = r.page_key || r.path || 'inconnue';
        const entry = pageMap.get(key) || { views: 0, ips: new Set<string>(), totalTime: 0, timeCount: 0 };
        entry.totalTime += dur;
        entry.timeCount += 1;
        pageMap.set(key, entry);
      });

      const pages: PageTrafficRow[] = Array.from(pageMap.entries())
        .map(([key, v]) => ({
          pageKey: key,
          views: v.views,
          uniqueVisitors: v.ips.size,
          totalTime: v.totalTime,
          avgTime: v.timeCount > 0 ? Math.round(v.totalTime / v.timeCount) : 0,
        }))
        .sort((a, b) => b.views - a.views);

      // Produits les plus consultés : chemins de type /product/123
      const productCount = new Map<number, number>();
      list.forEach((r) => {
        const p = r.path || '';
        const m = p.match(/\/product\/(\d+)/);
        if (m) {
          const id = parseInt(m[1], 10);
          if (!Number.isNaN(id)) productCount.set(id, (productCount.get(id) || 0) + 1);
        }
      });

      let topProducts: ProductTrafficRow[] = [];
      const productIds = Array.from(productCount.keys());
      if (productIds.length > 0) {
        const { data: prods } = await supabase
          .from('product_items')
          .select('id, name')
          .in('id', productIds);
        const nameMap = new Map<number, string>((prods || []).map((p: { id: number; name: string }) => [p.id, p.name]));
        topProducts = productIds
          .map((id) => ({ productId: id, name: nameMap.get(id) || `Produit #${id}`, views: productCount.get(id) || 0 }))
          .sort((a, b) => b.views - a.views)
          .slice(0, 6);
      }

      const totalTime = list
        .filter((r) => r.event === 'leave' || r.event === 'ping')
        .reduce((s, r) => s + (Number(r.duration_seconds) || 0), 0);
      const timeCount = list.filter((r) => (r.event === 'leave' || r.event === 'ping') && (Number(r.duration_seconds) || 0) > 0).length;

      const behavior: BehaviorRow[] = list.slice(0, 25).map((r) => ({
        id: r.id,
        pageKey: r.page_key,
        path: r.path,
        ip: r.ip,
        event: r.event,
        duration: Number(r.duration_seconds) || 0,
        at: r.created_at,
      }));

      setData({
        totalViews: viewRows.length,
        uniqueVisitors: ipSet.size,
        avgTime: timeCount > 0 ? Math.round(totalTime / timeCount) : 0,
        pages,
        topProducts,
        behavior,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement du trafic');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [user, days]);

  useEffect(() => {
    fetchTraffic();
  }, [fetchTraffic]);

  return { data, loading, error, reload: fetchTraffic };
}

/** Formate une durée en secondes vers un texte court (ex. « 1 min 20 s »). */
export function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m} min ${s} s` : `${m} min`;
}